import type { OnBoardingStore } from "@/Modules/OnBoarding/Store";
import type { Choice } from "@/Modules/Models/Domain/OnBoarding";
import type { UserStore } from "@/Modules/User/Store";
import type { Language } from "@/Common/Models/Domain/Language";
import {
  AnswersIds,
  YesOrNoAnswer,
  StepIdWithChoices,
  SUBAREA_NAME_THERAPIST_KNOWN,
  AREA_NAME_COACHING,
  EMPTY_AREA,
  AREA_NAME_COUPLE,
} from "@/Modules/Models/Domain/OnBoarding";

import useUserStore from "@/Modules/User/Store";
import { navigatorLang, normalizeLanguage } from "@/Utils/Language";

class SetAnswerActionHandler {
  constructor(
    private readonly onBoardingStore: OnBoardingStore,
    private readonly userStore: UserStore = useUserStore()
  ) {}

  public async handle(answerId: AnswersIds | string, value: string | number | boolean): Promise<void> {
    if (answerId === AnswersIds.LANGUAGE && typeof value === "string") {
      const language = normalizeLanguage(value);

      if (!language) return;

      await this.updateLanguage(language, () => {
        this.onBoardingStore.answers[AnswersIds.LANGUAGE] = language;
        this.onBoardingStore.resetTherapyLanguage();
        this.onBoardingStore.resetTherapyType();
      });

      return;
    }

    if (answerId === AnswersIds.THERAPY_LANGUAGE && typeof value === "string") {
      const languageChoice = this.onBoardingStore.getChoiceInfo(
        StepIdWithChoices.THERAPY_LANGUAGE,
        value,
        "uuid"
      );
      const language = normalizeLanguage(languageChoice?.extra) ?? navigatorLang;

      await this.updateLanguage(language, () => {
        this.onBoardingStore.answers[AnswersIds.THERAPY_LANGUAGE] = value;
        this.onBoardingStore.answers[AnswersIds.LANGUAGE] = language;
      });

      return;
    }

    if (answerId && value) {
      this.onBoardingStore.answers[answerId] = value;
    }

    if (answerId === AnswersIds.USERNAME && typeof value === "string") {
      this.onBoardingStore.username = value;

      if (this.userStore.isSSOPatient) {
        await this.onBoardingStore.setOptionsData(this.userStore.user?.language ?? navigatorLang);
      }
    }

    if (answerId === AnswersIds.THERAPY_TYPE && typeof value === "string") {
      this.onBoardingStore.areaSelected = "";
      delete this.onBoardingStore.answers[AnswersIds.MAIN_AREA];
      delete this.onBoardingStore.answers[AnswersIds.SUB_AREA];

      if (this.onBoardingStore.isCoachingSelected) {
        const area = this.onBoardingStore.getChoiceInfo(
          StepIdWithChoices.MAIN_AREA,
          AREA_NAME_COACHING,
          "name"
        );

        this.onBoardingStore.areaSelected = area?.uuid as string;
        this.onBoardingStore.resetTherapyLanguage();
      }

      if (this.onBoardingStore.isCoupleSelected) {
        const area = this.onBoardingStore.getChoiceInfo(
          StepIdWithChoices.MAIN_AREA,
          AREA_NAME_COUPLE,
          "name"
        );

        this.onBoardingStore.areaSelected = area?.uuid as string;
      }
    }

    if (answerId === AnswersIds.MAIN_AREA && typeof value === "string") {
      const subArea = this.getSubAreas(
        value,
        this.onBoardingStore.getStepChoices(StepIdWithChoices.SUB_AREA)
      );

      this.onBoardingStore.areaSelected = value;

      if (subArea && (subArea.name === SUBAREA_NAME_THERAPIST_KNOWN || subArea.name === EMPTY_AREA)) {
        this.onBoardingStore.answers.sub_area = subArea.uuid;
      }
    }

    if (answerId === "email" && typeof value === "string") {
      this.onBoardingStore.email = value;
    }

    if (answerId === AnswersIds.GO_TO_LAST_THERAPIST) {
      this.onBoardingStore.patientWantsLastTherapist = value === YesOrNoAnswer.YES;
    }
  }

  private getSubAreas(areaUuid: string, areas: Choice[]) {
    return areas.find((area) => area.mainArea === areaUuid);
  }

  private async updateLanguage(language: Language, updateAnswers: () => void): Promise<void> {
    const isLocaleActive = await this.userStore.updateLanguage(language);

    if (!isLocaleActive) return;

    updateAnswers();

    await this.onBoardingStore.setOptionsData(language);
  }
}

export default SetAnswerActionHandler;
